import React, { useState } from 'react';
import { Mail, MapPin, Phone, Github, Linkedin, Twitter, Send } from 'lucide-react';
import { contactData } from '../mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const iconMap = {
  Github: Github,
  Linkedin: Linkedin,
  Twitter: Twitter
};

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission
    toast({
      title: "Mensaje enviado!",
      description: "Gracias por contactarme. Te responderé pronto.",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  return (
    <section id="contacto" className="py-24 bg-black relative">
      <div className="absolute top-0 left-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-green-400 font-mono mb-4">
              {'<'} Contacto {'/>'}
            </h2>
            <p className="text-gray-400 font-mono text-lg">Conversemos sobre tu próximo proyecto</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="bg-gray-900/50 border-green-500/30">
                <CardHeader>
                  <CardTitle className="text-2xl text-green-400 font-mono">Información de contacto</CardTitle>
                  <CardDescription className="text-gray-400">Puedes contactarme a través de estos medios</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-green-400" size={24} />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm font-mono">Email</p>
                      <p className="text-white font-mono">{contactData.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-green-400" size={24} />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm font-mono">Teléfono</p>
                      <p className="text-white font-mono">{contactData.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-green-400" size={24} />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm font-mono">Ubicación</p>
                      <p className="text-white font-mono">{contactData.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Social Links */}
              <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-6">
                <h3 className="text-xl text-green-400 font-mono mb-4">Redes sociales</h3>
                <div className="flex gap-4">
                  {contactData.social.map((social) => {
                    const Icon = iconMap[social.icon];
                    return (
                      <a
                        key={social.name}
                        href={social.url}
                        className="w-12 h-12 bg-green-500/10 hover:bg-green-500/20 rounded-lg flex items-center justify-center transition-all transform hover:scale-110"
                        aria-label={social.name}
                      >
                        <Icon className="text-green-400" size={24} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="bg-gray-900/50 border-green-500/30">
              <CardHeader>
                <CardTitle className="text-2xl text-green-400 font-mono">Envíame un mensaje</CardTitle>
                <CardDescription className="text-gray-400">Completa el formulario y me pondré en contacto</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Tu nombre"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-gray-950 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400 font-mono"
                    />
                  </div>

                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Tu email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-gray-950 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400 font-mono"
                    />
                  </div>

                  <div>
                    <Input
                      type="text"
                      name="subject"
                      placeholder="Asunto"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="bg-gray-950 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400 font-mono"
                    />
                  </div>

                  <div>
                    <Textarea
                      name="message"
                      placeholder="Tu mensaje"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="bg-gray-950 border-green-500/30 text-white placeholder:text-gray-500 focus:border-green-400 font-mono resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-green-500 hover:bg-green-400 text-black font-mono font-bold text-lg py-6 transition-all transform hover:scale-105 shadow-lg shadow-green-500/50"
                  >
                    <Send className="mr-2" size={20} />
                    ENVIAR MENSAJE
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;